import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, CheckCircle2, RefreshCw, Users, MessageSquareQuote, Award } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Photo, StatusBadge, TypeBadge, usePriceText, useDate } from "@/components/dm/ui";
import { EmptyState, CreateButtons } from "@/components/dm/EmptyState";

const ContactCheck = ({ p, onDone }) => {
  const { t } = useApp();
  const answer = async (response) => { await api.post(`/posts/${p.id}/contact-check`, { response }); toast.success(t("thanks")); onDone(); };
  return (
    <div data-testid={`contact-check-${p.id}`} className="rounded-2xl bg-[#FEF3C7] border border-[#F59E0B]/60 p-3 space-y-2">
      <p className="font-bold text-sm">{t("cc_question")}</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`cc-yes-${p.id}`} onClick={() => answer("yes")} className="press min-h-[44px] px-3 rounded-full bg-[#1E4D3B] text-white text-sm font-bold">{t(p.type === "request" ? "cc_yes_request" : "cc_yes_listing")}</button>
        <button data-testid={`cc-not-yet-${p.id}`} onClick={() => answer("not_yet")} className="press min-h-[44px] px-3 rounded-full bg-[#FFFDF9] text-sm font-bold">{t("cc_not_yet")}</button>
        <button data-testid={`cc-close-${p.id}`} onClick={() => answer("close")} className="press min-h-[44px] px-3 rounded-full bg-[#FFFDF9] text-sm font-bold">{t("cc_close")}</button>
      </div>
    </div>
  );
};

const SpotlightAsk = ({ p, reload }) => {
  const { t } = useApp();
  const st = p.spotlight;
  if (st === "featured") return <span data-testid={`spotlight-status-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#F59E0B] text-[#291E18] px-3 py-1 text-xs font-extrabold"><Award className="w-4 h-4" />{t("sp_featured")}</span>;
  if (st === "pending") return <span data-testid={`spotlight-status-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#FEF3C7] text-[#92400E] px-3 py-1 text-xs font-bold"><Award className="w-4 h-4" />{t("sp_pending")}</span>;
  const ask = async () => {
    if (!window.confirm(t("sp_confirm"))) return;
    try { await api.post(`/posts/${p.id}/spotlight-request`); toast.success(t("sp_sent")); reload(); }
    catch (e) { toast.error(t(e?.response?.data?.detail === "already_pending" ? "sp_pending" : "error")); }
  };
  return (
    <button data-testid={`spotlight-ask-${p.id}`} onClick={ask} className="press min-h-[44px] px-3 rounded-full text-sm font-bold inline-flex items-center gap-1.5 bg-[#FFFDF9] ring-2 ring-[#F59E0B] text-[#92400E]">
      <Award className="w-4 h-4" />{t(st === "rejected" ? "sp_ask_again" : "sp_ask")}
    </button>
  );
};

const Row = ({ p, reload }) => {
  const { t, cat } = useApp();
  const price = usePriceText();
  const date = useDate();
  const navigate = useNavigate();
  const closed = p.is_expired || ["sold", "found", "hidden_pending_review"].includes(p.status);
  const contacts = p.contact_count === 0 ? t("contacts_0") : p.contact_count === 1 ? t("contacts_1") : t("contacts_n", { n: p.contact_count });
  const act = async (fn, msg) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch { toast.error(t("error")); } };
  const btn = "press min-h-[44px] px-3 rounded-full text-sm font-bold inline-flex items-center gap-1.5";
  return (
    <div data-testid={`my-post-${p.id}`} className="rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] card-shadow p-3 space-y-3">
      <Link to={`/post/${p.id}`} className="flex gap-3">
        {p.type === "request" ? <div className="w-20 h-20 rounded-2xl bg-[#FFF4EA] border-2 border-[#C2410C]/30 flex items-center justify-center shrink-0"><MessageSquareQuote className="w-7 h-7 text-[#C2410C]" /></div>
          : <Photo src={p.photos?.[0]} icon={cat(p.category_id)?.icon} className="w-20 h-20 rounded-2xl shrink-0" />}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex gap-1.5 flex-wrap"><TypeBadge type={p.type} /><StatusBadge status={p.is_expired && !["sold", "found"].includes(p.status) ? "expired" : p.status} /></div>
          <div className="font-bold line-clamp-2 leading-snug">{p.title}</div>
          <div className="text-sm font-bold text-[#92400E]">{price(p)}</div>
        </div>
      </Link>
      <div className="flex items-center justify-between text-sm">
        <span data-testid={`contact-count-${p.id}`} className="inline-flex items-center gap-1.5 font-semibold text-[#1E4D3B]"><Users className="w-4 h-4" />{contacts}</span>
        <span className="text-xs text-[#6B5E55]">{t("expires_on", { date: date(p.expires_at) })}</span>
      </div>
      {p.needs_contact_check && <ContactCheck p={p} onDone={reload} />}
      <div className="flex flex-wrap gap-2">
        {p.status !== "hidden_pending_review" && <button data-testid={`edit-${p.id}`} onClick={() => navigate(`/edit/${p.id}`)} className={`${btn} bg-[#F3EDE4]`}><Pencil className="w-4 h-4" />{t("edit")}</button>}
        {!closed && !p.is_demo && <SpotlightAsk p={p} reload={reload} />}
        {!closed && <button data-testid={`mark-closed-${p.id}`} onClick={() => act(() => api.post(`/posts/${p.id}/status`, { status: p.type === "request" ? "found" : "sold" }))} className={`${btn} bg-[#1E4D3B] text-white`}><CheckCircle2 className="w-4 h-4" />{t(p.type === "request" ? "mark_found" : "mark_sold")}</button>}
        {closed && p.status !== "hidden_pending_review" && <button data-testid={`renew-${p.id}`} onClick={() => act(() => api.post(`/posts/${p.id}/renew`), t("renewed"))} className={`${btn} bg-[#F59E0B] text-[#291E18]`}><RefreshCw className="w-4 h-4" />{p.is_expired ? t("renew") : t("reactivate")}</button>}
        <button data-testid={`delete-${p.id}`} onClick={() => window.confirm(t("confirm_delete")) && act(() => api.delete(`/posts/${p.id}`))} className={`${btn} text-[#B91C1C]`}><Trash2 className="w-4 h-4" />{t("delete")}</button>
      </div>
    </div>
  );
};

export default function MyPosts() {
  const { t, user, ready, setAuth } = useApp();
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("active");
  const load = () => api.get("/me/posts").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { if (user) load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!ready) return null;
  if (!user) return <div className="max-w-xl mx-auto p-6"><EmptyState title={t("signup_prompt")}><button data-testid="my-posts-signup" onClick={() => setAuth({ mode: "prompt" })} className="press min-h-[52px] px-8 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{t("create_account")}</button></EmptyState></div>;
  const isClosed = (p) => p.is_expired || ["sold", "found", "hidden_pending_review"].includes(p.status);
  const shown = (items || []).filter((p) => (tab === "active" ? !isClosed(p) : isClosed(p)));
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="my-posts-page">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("my_posts")}</h1>
      <div className="flex rounded-full bg-[#F3EDE4] p-1 w-fit">
        {["active", "closed"].map((k) => <button key={k} data-testid={`my-posts-tab-${k}`} onClick={() => setTab(k)} className={`press min-h-[44px] px-5 rounded-full font-bold ${tab === k ? "bg-[#1E4D3B] text-white" : "text-[#6B5E55]"}`}>{t(`tab_${k}`)}</button>)}
      </div>
      {items === null ? <div className="skeleton h-32 rounded-[20px]" /> : shown.length ? <div className="grid gap-4 sm:grid-cols-2">{shown.map((p) => <Row key={p.id} p={p} reload={load} />)}</div>
        : <EmptyState title={t(tab === "active" ? "no_posts_yet" : "no_closed_posts")}><CreateButtons /></EmptyState>}
    </div>
  );
}
